<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { gwI18nContextToken } from "@/context_tokens";
    import MediaForm from "@apps/reviews/components/ReviewForm/MediaForm.svelte";
    import FormManager from "@lib/FormManager/FormManager";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import FileUploaderManager from "@modules/file_uploader/FileUploaderManager/FileUploaderManager";
    import ImageFileValidator from "@modules/file_uploader/FileUploaderManager/validators/ImageFileValidator";
    import { FileUploaderModule } from "@modules/file_uploader/FileUploaderModule";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { map } from "rxjs/operators";
    import { noop } from "svelte/internal";
    import { container } from "tsyringe";
    import * as Yup from "yup";

    import { stepperControllerContextToken } from "@components/Stepper/context_tokens";
    import T from "@components/T.svelte";

    import { REVIEWS_FORM_STEPS_NAMES } from "../../../constants";
    import ReviewsWizardManager from "../../../managers/ReviewsWizardManager";
    import LeaveReviewEarningRuleQuery from "../../../queries/LeaveReviewEarningRuleQuery";

    export let reviewsWizardManager: ReviewsWizardManager;
    export let leaveReviewEarningRuleQuery: LeaveReviewEarningRuleQuery;

    const stepperController = getTypedContext(stepperControllerContextToken);
    const stepList$ = stepperController.stepList$;
    const gwI18n = getTypedContext(gwI18nContextToken);

    const isLastFormStep$ = stepList$.pipe(
        map(
            (arr) =>
                arr.length >= 2 &&
                arr[arr.length - 2].name === REVIEWS_FORM_STEPS_NAMES.MEDIA
        )
    );

    const fileUploaderManager =
        reviewsWizardManager.cacheManager.get<FileUploaderManager>(
            "fileUploaderManager",
            [],
            () => {
                const validator = new ImageFileValidator(gwI18n);
                const fileUploaderManager = new FileUploaderManager(
                    validator,
                    container.resolve(
                        FileUploaderModule
                    ).exports.fileUploaderService,
                    {
                        maxNumberOfFiles: 5,
                        appName: "reviews",
                    }
                );

                return fileUploaderManager;
            }
        );

    const formManager = reviewsWizardManager.cacheManager.get(
        REVIEWS_FORM_STEPS_NAMES.MEDIA,
        [$isLastFormStep$],
        () => {
            return new FormManager({
                initialValues: { images: [] },
                resolver: yupResolver(
                    Yup.object({
                        images: Yup.array(
                            Yup.object({
                                key: Yup.string(),
                                bucket: Yup.string(),
                            })
                        ),
                    })
                ),
                validationStrategy: ValidationStrategy.OnSubmit,
                unregisterOnUnmount: false,
                onSubmit: ({ values, errors }) => {
                    if (!values || (errors && Object.values(errors).length > 0))
                        return;

                    reviewsWizardManager.collectFormData(values);
                    if ($isLastFormStep$) {
                        reviewsWizardManager.submitCollectedFormData();
                    } else {
                        stepperController.nextStep();
                    }
                },
            });
        }
    );

    const isFilesLoading$ = fileUploaderManager.files$.pipe(
        map((files) => files.some((file) => file.status === STATUS.LOADING))
    );
    const images$ = formManager.selectValue("images");
    const fileErrors$ = fileUploaderManager.validationErrors$;

    const setNavButtons = () => {
        reviewsWizardManager.setPrevButton({
            type: "prev",
            isActive: true,
            onClick: () => {
                stepperController.prevStep();
            },
        });

        if ($isLastFormStep$) {
            reviewsWizardManager.setNextButton({
                type: "submit",
                isActive: !$isFilesLoading$,
                onClick: () => {
                    formManager.submit();
                },
            });
            return;
        }

        if ($isFilesLoading$) {
            reviewsWizardManager.setNextButton({
                type: "next",
                isActive: false,
                onClick: noop,
            });
            return;
        }

        if ($images$?.length) {
            reviewsWizardManager.setNextButton({
                type: "next",
                isActive: true,
                onClick: () => {
                    formManager.submit();
                },
            });
            return;
        }

        reviewsWizardManager.setNextButton({
            type: "skip",
            isActive: true,
            onClick: () => {
                formManager.submit();
            },
        });
    };

    $: {
        $images$;
        $isFilesLoading$;
        $isLastFormStep$;
        setNavButtons();
    }

    const i18nModule = container.resolve(I18nModule);
    const t_points_name$ = i18nModule.exports.i18n.selectT(
        "common.critical.Points",
        "points_name_plural"
    );

    const leaveReviewEarningRule$ =
        leaveReviewEarningRuleQuery.leaveReviewEarningRule$;
    const previewImages$ = fileUploaderManager.files$;
</script>

<div class="gw-rv-form-photo">
    <div class="gw-rv-form-photo__header">
        <h2 class="gw-rv-form-photo__title">
            <T
                namespace="reviews.ReviewsFormModalWidget"
                translationKey="add_photo_step_title"
            />
        </h2>
        <h3 class="gw-rv-form-photo__subtitle">
            <T
                namespace="reviews.ReviewsFormModalWidget"
                translationKey="media_step_sub_title"
            />
        </h3>
    </div>
    <div class="gw-rv-form-photo__content">
        <div class="gw-rv-form-photo__points">
            {#if $leaveReviewEarningRule$?.extraPointsForPhoto}
                <p class="gw-rv-form-photo__points-text">
                    <T
                        namespace="reviews.ReviewsFormModalWidget"
                        translationKey="photo_step_extra_points"
                        translationOptions={{
                            points: $leaveReviewEarningRule$.extraPointsForPhoto,
                            points_name: $t_points_name$,
                        }}
                    />
                </p>
            {/if}
        </div>
        <div class="gw-rv-form-photo__media">
            <MediaForm
                {formManager}
                {fileUploaderManager}
                previewImages={$previewImages$}
                fileErrors={$fileErrors$}
            />
        </div>
    </div>
</div>

<style lang="postcss">
    .gw-rv-form-photo {
        padding-top: 20px;
        max-width: 570px;

        &__content {
            display: flex;
            flex-direction: column;
            align-items: center;
            border-radius: 0.625em;
            margin-top: 2em;
            gap: 1.5em;
        }

        &__points {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1em;
            margin: 0;
        }

        &__points-text {
            font-size: 1.2em;
            text-align: center;
            margin: 0;
            line-height: 1.8;
        }

        &__title {
            text-align: center;
            font-size: 1.8em;
            line-height: 1.2;
            font-weight: 600;
            margin: 0;
        }

        &__subtitle {
            text-align: center;
            margin: 0.7em 0 0;
            font-size: 1.2em;
            font-style: normal;
            font-weight: 400;
            line-height: 1.8;
            padding: 0 2em;

            @media screen and (max-width: 600px) {
                padding: 0;
            }
        }

        &__media {
            width: 70%;
        }
    }
</style>
